/**
 * Adapter `alignment.json` → `KaraokeLayout` + cálculo de la palabra/oración
 * activa (`activeIndices`).
 *
 * El prototipo de diseño SINTETIZA el timing (rate por nivel + clock RAF). Acá
 * el timing es REAL: viene del forced-alignment (19.B) y se consume contra
 * `audio.currentTime`. El shape `alignment.json` (ver
 * `content/book-summaries/scripts/generate-alignment.mjs`):
 *
 *   { durationSec, loss, words:[{w,start,end}],
 *     sentences:[{text,start,end,wordRange:[i0,i1]}] }
 *
 * Cada `sentence` → una `line`; sus palabras = `words[wordRange[0..1]]`. Los
 * timestamps están en la línea de tiempo ORIGINAL, así que el resaltado NO se
 * desfasa al cambiar `playbackRate` (currentTime también es timeline original).
 */

import { normalizeWord } from '../../components/word-lookup/index.js';
import type { KaraokeLayout } from './types.js';

interface AlignmentWord {
  w: string;
  start: number;
  end: number;
}

interface AlignmentSentence {
  text: string;
  start: number;
  end: number;
  wordRange: [number, number];
}

export interface AlignmentJson {
  durationSec: number;
  loss?: number;
  words: AlignmentWord[];
  sentences: AlignmentSentence[];
}

/** Convierte el `alignment.json` crudo al `KaraokeLayout` que consume la UI. */
export function alignmentToLayout(a: AlignmentJson): KaraokeLayout {
  return {
    duration: a.durationSec,
    lines: a.sentences.map((s, li) => ({
      li,
      start: s.start,
      end: s.end,
      text: s.text,
      words: a.words.slice(s.wordRange[0], s.wordRange[1] + 1).map((w) => ({
        text: w.w,
        start: w.start,
        end: w.end,
      })),
    })),
  };
}

export interface ActiveIndices {
  /** Índice de línea activa. -1 si `pos` cae antes de la primera línea. */
  lineIdx: number;
  /**
   * Índice de palabra activa dentro de la línea. -1 antes de la primera palabra;
   * `>= words.length` cuando ya terminó la línea (gap entre oraciones).
   */
  wordIdx: number;
  /** `pos` cae antes de que empiece a sonar la transcripción. */
  before: boolean;
  /** `pos` cae en el hueco entre el fin de la línea y la siguiente. */
  between: boolean;
}

/**
 * Dado un `layout` y la posición `pos` (segundos, = audio.currentTime), calcula
 * qué línea y palabra están activas. Función pura — la consume `KaraokeText`.
 *
 * Búsqueda lineal (las transcripciones son cortas, ~17 líneas / ~190 palabras).
 */
export function activeIndices(layout: KaraokeLayout, pos: number): ActiveIndices {
  const { lines } = layout;
  if (lines.length === 0) return { lineIdx: -1, wordIdx: -1, before: true, between: false };

  const firstLine = lines[0];
  if (firstLine && pos < firstLine.start) {
    return { lineIdx: -1, wordIdx: -1, before: true, between: false };
  }

  // Línea activa: la última cuyo `start <= pos`.
  let lineIdx = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line && line.start <= pos) lineIdx = i;
    else break;
  }

  const line = lines[lineIdx];
  if (!line) return { lineIdx: -1, wordIdx: -1, before: true, between: false };

  // Si `pos` pasó el fin de la línea → estamos en el hueco hacia la siguiente.
  if (pos >= line.end) {
    return { lineIdx, wordIdx: line.words.length, before: false, between: true };
  }

  // Palabra activa: la última cuyo `start <= pos`.
  let wordIdx = -1;
  for (let j = 0; j < line.words.length; j++) {
    const word = line.words[j];
    if (word && word.start <= pos) wordIdx = j;
    else break;
  }

  return { lineIdx, wordIdx, before: false, between: false };
}

/**
 * Palabras únicas (normalizadas) de un layout — el universo del glosario del
 * capítulo (tap-to-lookup). Se enumeran del MISMO layout que se renderiza
 * (garantía de cobertura). Ver `chapter-glossary.ts` en el backend.
 */
export function collectGlossaryWords(lay: KaraokeLayout): string[] {
  const set = new Set<string>();
  for (const line of lay.lines) {
    for (const w of line.words) {
      const n = normalizeWord(w.text);
      if (n) set.add(n);
    }
  }
  return [...set];
}
